import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

const openingHours = [
  { day: 'Montag', hours: '10:00 – 19:00 Uhr', open: true },
  { day: 'Dienstag', hours: '10:00 – 19:00 Uhr', open: true },
  { day: 'Mittwoch', hours: '10:00 – 19:00 Uhr', open: true },
  { day: 'Donnerstag', hours: '10:00 – 19:00 Uhr', open: true },
  { day: 'Freitag', hours: '10:00 – 19:00 Uhr', open: true },
  { day: 'Samstag', hours: '10:00 – 16:00 Uhr', open: true },
  { day: 'Sonntag', hours: 'Geschlossen', open: false },
];

const today = new Date().toLocaleDateString('de-DE', { weekday: 'long' });

export default function Standort() {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  usePageMeta({ canonical: '/standort' });

  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'HealthAndBeautyBusiness',
      name: 'Flow & Glow – Kosmetische Zahnaufhellung Köln',
      description: 'Boutique-Studio für kosmetische Zahnaufhellung in Köln. Schmerzfrei, peroxidfrei mit PAP-Technologie. Termine ab 99 €.',
      url: siteUrl || 'https://flowundglow.de',
      telephone: '+49 221 0000000',
      email: 'hallo@flowundglow.de',
      priceRange: '€€',
      image: `${siteUrl || 'https://flowundglow.de'}/og-image.jpg`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Köln Innenstadt',
        addressLocality: 'Köln',
        postalCode: '50667',
        addressRegion: 'Nordrhein-Westfalen',
        addressCountry: 'DE',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 50.9333,
        longitude: 6.9500,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '10:00',
          closes: '19:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '10:00',
          closes: '16:00',
        },
      ],
      sameAs: ['https://www.instagram.com/flowundglow'],
      hasMap: 'https://maps.google.com/?q=Köln+Innenstadt',
      areaServed: {
        '@type': 'City',
        name: 'Köln',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Startseite', item: siteUrl || 'https://flowundglow.de' },
        { '@type': 'ListItem', position: 2, name: 'Standort Köln', item: `${siteUrl || 'https://flowundglow.de'}/standort` },
      ],
    },
  ]);

  return (
    <main className="font-sans bg-offwhite min-h-screen">
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-offwhite border-b border-charcoal/8 pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3">
          <ol className="flex items-center gap-2 flex-wrap">
            <li>
              <Link to="/" className="font-sans text-xs text-charcoal/50 hover:text-rose transition-colors duration-200 cursor-pointer">
                Startseite
              </Link>
            </li>
            <li className="flex items-center">
              <span className="w-3 h-3 flex items-center justify-center text-charcoal/30">
                <i className="ri-arrow-right-s-line text-xs" />
              </span>
            </li>
            <li>
              <span className="font-sans text-xs text-charcoal font-medium">Standort Köln</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-charcoal pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/b1b5900a3d8daef6036c523396335421.jpg"
            alt="Flow & Glow Standort Köln – Kosmetische Zahnaufhellung"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/20 to-charcoal/50" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center pt-16">
          <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
            Unser Studio
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-white mt-3 mb-5 leading-tight">
            Flow &amp; Glow in Köln
          </h1>
          <p className="font-sans text-base md:text-lg text-white/60 max-w-xl mx-auto leading-relaxed">
            Dein Boutique-Studio für <strong className="text-white/80">kosmetische Zahnaufhellung in Köln</strong> — zentral gelegen, entspannte Atmosphäre, persönliche Betreuung.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

            {/* Left: Info Cards */}
            <div className="lg:col-span-2 flex flex-col gap-5">

              {/* Adresse */}
              <div className="bg-white rounded-2xl p-6 ring-1 ring-charcoal/8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-2-line text-rose text-lg" />
                  </div>
                  <h2 className="font-sans font-semibold text-base text-charcoal">Adresse</h2>
                </div>
                <div className="font-sans text-sm text-charcoal/70 leading-relaxed space-y-1">
                  <p className="font-semibold text-charcoal">Flow &amp; Glow</p>
                  <p>Köln Innenstadt</p>
                  <p>50667 Köln</p>
                  <p className="text-charcoal/40 text-xs italic mt-2">Genaue Adresse folgt in Kürze</p>
                </div>
                <a
                  href="https://maps.google.com/?q=Köln+Innenstadt"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1.5 mt-4 font-sans text-sm text-rose hover:text-rose-dark transition-colors duration-200 cursor-pointer font-medium"
                >
                  In Google Maps öffnen
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm" />
                  </span>
                </a>
              </div>

              {/* Öffnungszeiten */}
              <div className="bg-white rounded-2xl p-6 ring-1 ring-charcoal/8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                    <i className="ri-time-line text-rose text-lg" />
                  </div>
                  <h2 className="font-sans font-semibold text-base text-charcoal">Öffnungszeiten</h2>
                </div>
                <div className="flex flex-col gap-2">
                  {openingHours.map((row) => {
                    const isToday = today === row.day;
                    return (
                      <div
                        key={row.day}
                        className={`flex items-center justify-between py-1.5 px-3 rounded-lg transition-colors ${
                          isToday ? 'bg-rose/8' : ''
                        }`}
                      >
                        <span className={`font-sans text-sm ${isToday ? 'font-semibold text-charcoal' : 'text-charcoal/70'}`}>
                          {row.day}
                          {isToday && (
                            <span className="ml-2 text-[10px] font-semibold text-rose uppercase tracking-wide">Heute</span>
                          )}
                        </span>
                        <span className={`font-sans text-sm ${row.open ? (isToday ? 'text-charcoal font-semibold' : 'text-charcoal/70') : 'text-charcoal/35'}`}>
                          {row.hours}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="font-sans text-xs text-charcoal/40 mt-3 italic">Nur nach Terminvereinbarung</p>
              </div>

              {/* Anreise */}
              <div className="bg-white rounded-2xl p-6 ring-1 ring-charcoal/8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                    <i className="ri-train-line text-rose text-lg" />
                  </div>
                  <h2 className="font-sans font-semibold text-base text-charcoal">Anreise</h2>
                </div>
                <div className="flex flex-col gap-3">
                  {[
                    { icon: 'ri-bus-line', label: 'ÖPNV', desc: 'Gut erreichbar per KVB (U-Bahn, Straßenbahn, Bus)' },
                    { icon: 'ri-car-line', label: 'PKW', desc: 'Parkplätze und Parkhäuser in unmittelbarer Nähe' },
                    { icon: 'ri-walk-line', label: 'Zu Fuß', desc: 'Zentrale Lage – fußläufig von der Innenstadt' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="w-7 h-7 flex items-center justify-center bg-rose/8 rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-rose text-sm`} />
                      </span>
                      <div>
                        <p className="font-sans text-sm font-semibold text-charcoal">{item.label}</p>
                        <p className="font-sans text-xs text-charcoal/60 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kontakt */}
              <div className="bg-charcoal rounded-2xl p-6">
                <h2 className="font-sans font-semibold text-base text-white mb-4">Direkt Kontakt aufnehmen</h2>
                <div className="flex flex-col gap-3">
                  <a
                    href="https://wa.me/4922100000"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors duration-200 cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-white/10 group-hover:bg-white/20 rounded-lg transition-colors">
                      <i className="ri-whatsapp-line text-base" />
                    </span>
                    <span className="font-sans text-sm">WhatsApp schreiben</span>
                  </a>
                  <a
                    href="mailto:hallo@flowundglow.de"
                    className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors duration-200 cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-white/10 group-hover:bg-white/20 rounded-lg transition-colors">
                      <i className="ri-mail-line text-base" />
                    </span>
                    <span className="font-sans text-sm">hallo@flowundglow.de</span>
                  </a>
                  <a
                    href="https://www.instagram.com/flowundglow"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors duration-200 cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-white/10 group-hover:bg-white/20 rounded-lg transition-colors">
                      <i className="ri-instagram-line text-base" />
                    </span>
                    <span className="font-sans text-sm">@flowundglow</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Map + Studio Images */}
            <div className="lg:col-span-3 flex flex-col gap-5">

              {/* Google Maps */}
              <div className="rounded-2xl overflow-hidden ring-1 ring-charcoal/8" style={{ height: '420px' }}>
                <iframe
                  title="Flow & Glow – Standort Köln Innenstadt"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d80434.72375789786!2d6.887213!3d50.938361!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bf25aa4e37d2cb%3A0xf50db4c5ffe2f0ac!2sK%C3%B6ln!5e0!3m2!1sde!2sde!4v1711900000000!5m2!1sde!2sde"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-label="Google Maps – Flow & Glow Standort Köln"
                />
              </div>

              {/* Studio Impressions */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20minimalist%20beauty%20studio%20interior%2C%20warm%20cream%20and%20rose%20gold%20tones%2C%20professional%20cosmetic%20treatment%20room%2C%20soft%20ambient%20lighting%2C%20clean%20modern%20aesthetic%2C%20luxury%20boutique%20studio%2C%20comfortable%20treatment%20chair%2C%20premium%20beauty%20salon%20atmosphere%2C%20feminine%20and%20sophisticated%20decor&width=600&height=400&seq=standort-studio-01&orientation=landscape"
                    alt="Flow & Glow Studio Innenansicht – Kosmetische Zahnaufhellung Köln"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20beauty%20treatment%20room%20with%20professional%20whitening%20equipment%2C%20warm%20blush%20and%20cream%20interior%20design%2C%20elegant%20cosmetic%20studio%2C%20soft%20natural%20light%20from%20window%2C%20premium%20dental%20whitening%20setup%2C%20clean%20and%20inviting%20atmosphere%2C%20high%20end%20boutique%20studio%2C%20rose%20gold%20accents&width=600&height=400&seq=standort-studio-02&orientation=landscape"
                    alt="Flow & Glow Behandlungsraum – Zahnaufhellung Köln"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Service Area Info */}
              <div className="bg-white rounded-2xl p-6 ring-1 ring-charcoal/8">
                <h2 className="font-sans font-semibold text-base text-charcoal mb-4">
                  Einzugsgebiet &amp; Servicebereich
                </h2>
                <p className="font-sans text-sm text-charcoal/70 leading-relaxed mb-4">
                  Flow &amp; Glow ist dein Studio für <strong>kosmetische Zahnaufhellung in Köln</strong> und Umgebung. Kund*innen kommen zu uns aus dem gesamten Kölner Stadtgebiet sowie aus den umliegenden Städten:
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Köln Innenstadt', 'Köln-Ehrenfeld', 'Köln-Nippes', 'Köln-Lindenthal', 'Köln-Deutz', 'Leverkusen', 'Bonn', 'Bergisch Gladbach', 'Pulheim', 'Frechen'].map((city) => (
                    <span
                      key={city}
                      className="inline-flex items-center gap-1 bg-blush text-charcoal/80 font-sans text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-map-pin-line text-rose text-xs" />
                      </span>
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blush py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal mb-4 leading-tight">
            Bereit, uns zu besuchen?
          </h2>
          <p className="font-sans text-base text-charcoal/65 leading-relaxed mb-8 max-w-lg mx-auto">
            Buche jetzt deinen Termin online — in unter 2 Minuten. Wir freuen uns auf dich in unserem Studio in Köln.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/termin"
              className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line text-base" />
              </span>
              Termin buchen
            </Link>
            <a
              href="https://wa.me/4922100000"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-charcoal text-charcoal hover:bg-charcoal/5 font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base" />
              </span>
              WhatsApp schreiben
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}